import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { AutonubilVulnChain } from "./vuln-chain";
import { AutonubilNetworkFlows } from "./network-flows-diagram";
import { AutonubilCertSorting } from "./cert-sorting";

/*
 * "opsanio in action" - three animated product views ported from the presentation,
 * behind a lightweight tab switcher: vulnerability scan chain, network flows and
 * certificate expiry sorting. Each renders on the theme-independent navy "stage"
 * (works in light + dark); all motion is reduced-motion-gated (homepage-theme.css).
 */

const tPrefix = "anketa.autonubil.home.action";

type TabKey = "vuln" | "flows" | "certs";

const TABS: Array<{ key: TabKey; labelFb: string; captionFb: string; render: () => JSX.Element }> = [
  {
    key: "vuln",
    labelFb: "Schwachstellen",
    captionFb: "From asset to vulnerability: detected software matched against CVE sources, down to the concretely affected systems.",
    render: () => <AutonubilVulnChain />,
  },
  {
    key: "flows",
    labelFb: "Netzwerk-Flows",
    captionFb: "Who talks to whom, over which ports - on-premise and into the cloud.",
    render: () => <AutonubilNetworkFlows />,
  },
  {
    key: "certs",
    labelFb: "Zertifikate",
    captionFb: "Every certificate, grouped by remaining runtime - no expiry goes unnoticed.",
    render: () => <AutonubilCertSorting />,
  },
];

export const AutonubilDiagramsInAction: React.FC = () => {
  const { translate } = useAnketaI18n();
  const [active, setActive] = React.useState<TabKey>("vuln");
  const t = (k: string, d: string) => translate(`${tPrefix}.${k}`, { defaultMessage: d } as any);
  const current = TABS.find((tab) => tab.key === active) ?? TABS[0];

  return (
    <section
      style={{
        background: "var(--ak-page-bg)",
        padding: "clamp(64px, 9vw, 104px) clamp(24px, 5vw, 48px)",
        borderTop: "1px solid var(--ak-border)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* header */}
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--ak-accent)",
            }}
          >
            {t("eyebrow", "Under the hood")}
          </p>
          <h2
            style={{
              margin: "12px 0 0",
              fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)",
              fontWeight: 800,
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
              color: "var(--ak-text)",
            }}
          >
            {t("title", "From detection to decision")}
          </h2>
          <p style={{ margin: "16px auto 0", fontSize: "1.05rem", lineHeight: 1.65, color: "var(--ak-text-secondary)" }}>
            {t("subtitle", "The same unified data, seen three ways - vulnerabilities, network flows and certificates.")}
          </p>
        </div>

        {/* tabs */}
        <div
          role="tablist"
          aria-label={t("tablist", "Product views")}
          style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, margin: "32px 0 24px" }}
        >
          {TABS.map((tab) => {
            const isActive = tab.key === active;
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(tab.key)}
                style={{
                  cursor: "pointer",
                  padding: "10px 20px",
                  borderRadius: 999,
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  color: isActive ? "var(--ak-text-on-accent)" : "var(--ak-text-secondary)",
                  background: isActive ? "var(--ak-accent)" : "var(--ak-surface)",
                  border: `1px solid ${isActive ? "var(--ak-accent)" : "var(--ak-border)"}`,
                  boxShadow: isActive ? "0 6px 18px var(--ak-accent-glow)" : "none",
                  transition: "background 0.2s ease, color 0.2s ease, border-color 0.2s ease",
                }}
              >
                {t(`${tab.key}.tab`, tab.labelFb)}
              </button>
            );
          })}
        </div>

        {/* navy stage */}
        <div
          style={{
            position: "relative",
            borderRadius: 20,
            padding: "clamp(16px, 3vw, 30px)",
            background: "var(--ak-hero-bg)",
            border: "1px solid var(--ak-on-navy-border)",
            boxShadow: "0 12px 40px var(--ak-shadow-strong)",
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              backgroundImage:
                "linear-gradient(var(--ak-hero-grid) 1px, transparent 1px), linear-gradient(90deg, var(--ak-hero-grid) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
              WebkitMaskImage: "radial-gradient(70% 60% at 50% 45%, #000, transparent 82%)",
              maskImage: "radial-gradient(70% 60% at 50% 45%, #000, transparent 82%)",
            }}
          />
          {/* keyed so a tab change remounts and replays the slide-in (.ak-fade,
              reduced-motion-gated in homepage-theme.css) */}
          <div key={active} className="ak-fade" style={{ position: "relative" }} role="tabpanel">
            {current.render()}
          </div>
        </div>

        {/* caption */}
        <p
          style={{
            textAlign: "center",
            maxWidth: 720,
            margin: "20px auto 0",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            color: "var(--ak-text-secondary)",
          }}
        >
          {t(`${current.key}.caption`, current.captionFb)}
        </p>
      </div>
    </section>
  );
};

export default AutonubilDiagramsInAction;
